import React, { memo, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import type { DatePickerProps } from "antd";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { FieldWrapper } from "../FieldWrapper";
import {
  FieldComponentProps,
  DateFieldConfig,
  DatePickerMode,
} from "models/formField";
import { getFieldError } from "helpers/formField.components.helper";
import styles from "./DatePickerField.module.scss";

/**
 * DatePickerField - Date selection component integrated with React Hook Form
 * Uses Ant Design DatePicker
 */
export const DatePickerField: React.FC<FieldComponentProps<DateFieldConfig>> =
  memo(({ config, control: controlProp, errors: errorsProp }) => {
    // Get form context if available
    const formContext = useFormContext();
    const control = controlProp ?? formContext?.control;
    const errors = errorsProp ?? formContext?.formState?.errors;

    const error = getFieldError(errors, config.name);

    /**
     * Get the appropriate date format based on mode
     */
    const dateFormat = useMemo((): string => {
      if (config.dateFormat) return config.dateFormat;

      switch (config.mode) {
        case DatePickerMode.YEAR:
          return "YYYY";
        case DatePickerMode.MONTH:
          return "MM/YYYY";
        case DatePickerMode.WEEK:
          return "MM/DD/YYYY";
        default:
          return config.showTime ? "MM/DD/YYYY h:mm A" : "MM/DD/YYYY";
      }
    }, [config.dateFormat, config.mode, config.showTime]);

    /**
     * Get the picker type based on mode
     */
    const pickerType = useMemo((): DatePickerProps["picker"] => {
      switch (config.mode) {
        case DatePickerMode.YEAR:
          return "year";
        case DatePickerMode.MONTH:
          return "month";
        case DatePickerMode.WEEK:
          return "week";
        default:
          return "date";
      }
    }, [config.mode]);

    if (!control) {
      console.error(
        `DatePickerField "${config.name}" requires control prop or FormProvider`,
      );
      return null;
    }

    return (
      <Controller
        name={config.name}
        control={control}
        defaultValue={config.defaultValue}
        rules={config.validation}
        render={({ field }) => (
          <FieldWrapper
            label={config.label}
            name={config.name}
            error={error}
            tooltip={config.tooltip}
            required={!!config.validation?.required}
            className={config.className}
            hidden={config.hidden}
          >
            <DatePicker
              id={config.name}
              value={field.value ? dayjs(field.value) : null}
              onChange={(date: Dayjs | null) => {
                const dateValue = date ? date.toDate() : null;
                field.onChange(dateValue);
                if (dateValue) {
                  config.onChange?.(dateValue);
                  config.onDateSelect?.(dateValue);
                }
              }}
              onBlur={() => {
                field.onBlur();
                config.onBlur?.();
              }}
              placeholder={config.placeholder}
              disabled={config.disabled}
              inputReadOnly={config.readOnly}
              minDate={config.minDate ? dayjs(config.minDate) : undefined}
              maxDate={config.maxDate ? dayjs(config.maxDate) : undefined}
              format={dateFormat}
              showTime={config.showTime}
              picker={pickerType}
              className={styles.datepicker}
              popupClassName={styles.popup}
              status={error ? "error" : undefined}
              aria-invalid={!!error}
              aria-describedby={error ? `${config.name}-error` : undefined}
              style={{ width: "100%" }}
              {...config.data}
              defaultPickerValue={config?.defaultPickerValue}
            />
          </FieldWrapper>
        )}
      />
    );
  });

DatePickerField.displayName = "DatePickerField";
